import React, { useState, useEffect, useCallback } from 'react';
import { X, UserPlus, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from './ImpactDashboardTab';

const STATUSES = [
  ['not_recorded', 'Not recorded'], ['attended', 'Attended'], ['partially_attended', 'Partially attended'],
  ['no_show', 'No-show'], ['excused', 'Excused'],
];
const COMPLETION = [['not_assessed', 'Not assessed'], ['pending', 'Pending'], ['completed', 'Completed'], ['not_completed', 'Not completed']];

export default function ImpactRoster({ API_URL, getAuthHeader, event, onClose }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [walkin, setWalkin] = useState(null);

  const fetchRoster = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/impact/events/${event.id}/roster`, { headers: getAuthHeader() });
    if (r.ok) setData(await r.json());
  }, [API_URL, getAuthHeader, event.id]);

  useEffect(() => { fetchRoster(); }, [fetchRoster]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setStatus = async (pid, field, value) => {
    const r = await fetch(`${API_URL}/api/impact/participations/${pid}`, {
      method: 'PATCH', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value })
    });
    if (r.ok) fetchRoster();
    else toast.error('Update failed');
  };

  const bulk = async (status, ids) => {
    if (!ids.length) return toast.error('Select attendees first');
    const r = await fetch(`${API_URL}/api/impact/events/${event.id}/attendance/bulk`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ participation_ids: ids, attendance_status: status })
    });
    if (r.ok) { const d = await r.json(); toast.success(`${d.updated} record(s) marked ${status.replace('_', ' ')}`); setSelected(new Set()); fetchRoster(); }
    else toast.error('Bulk update failed');
  };

  const addWalkin = async () => {
    if (!walkin.name.trim()) return toast.error('Name is required');
    const r = await fetch(`${API_URL}/api/impact/events/${event.id}/walkins`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(walkin)
    });
    if (r.ok) { toast.success('Walk-in added as attended'); setWalkin(null); fetchRoster(); }
    else toast.error('Could not add walk-in');
  };

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="impact-roster-modal">
        <div className="px-6 py-4 flex items-start justify-between text-white rounded-t-xl sticky top-0 z-10" style={{ background: BRAND.navy }}>
          <div>
            <h3 className="font-bold text-lg" style={{ fontFamily: 'Roboto, Arial, sans-serif' }}>Roster — {event.title}</h3>
            <p className="text-xs text-slate-300">{event.date || 'Ongoing / undated'} · Attendance corrections are logged in the audit trail</p>
          </div>
          <button onClick={onClose} data-testid="impact-roster-close" className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!data ? <p className="p-10 text-center text-sm text-slate-400">Loading roster…</p> : (
          <div className="p-6 space-y-4">
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="px-3 py-1.5 rounded-lg bg-slate-100 font-semibold text-slate-600">{data.summary.total} on roster</span>
              <span className="px-3 py-1.5 rounded-lg font-semibold" style={{ background: '#f0f9e8', color: '#4d7c0f' }}>{data.summary.attended} attended</span>
              <span className="px-3 py-1.5 rounded-lg bg-slate-100 font-semibold text-slate-500">{data.summary.not_recorded} not recorded</span>
              {data.summary.no_show > 0 && <span className="px-3 py-1.5 rounded-lg bg-rose-50 font-semibold text-rose-600">{data.summary.no_show} no-show</span>}
              {data.summary.walk_ins > 0 && <span className="px-3 py-1.5 rounded-lg font-semibold" style={{ background: '#fdf6e3', color: '#927320' }}>{data.summary.walk_ins} walk-in(s) — reported separately</span>}
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={() => bulk('attended', [...selected])} data-testid="impact-roster-bulk-attended"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white" style={{ background: BRAND.teal }}>
                <CheckCheck className="w-3.5 h-3.5" /> Mark selected attended ({selected.size})
              </button>
              <button onClick={() => bulk('attended', data.roster.filter(r => r.attendance_status === 'not_recorded').map(r => r.id))}
                data-testid="impact-roster-bulk-all"
                className="px-3 py-2 rounded-lg text-xs font-bold border border-slate-300 text-slate-600 hover:bg-slate-50">
                Mark all “not recorded” as attended
              </button>
              <button onClick={() => setWalkin({ name: '', email: '', organization: '' })} data-testid="impact-roster-add-walkin"
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold ml-auto" style={{ background: '#fdf6e3', color: '#927320' }}>
                <UserPlus className="w-3.5 h-3.5" /> Add walk-in
              </button>
            </div>

            {walkin && (
              <div className="grid md:grid-cols-4 gap-2 border border-amber-200 bg-amber-50/50 rounded-lg p-3" data-testid="impact-walkin-form">
                <input placeholder="Name *" value={walkin.name} onChange={e => setWalkin(w => ({ ...w, name: e.target.value }))} data-testid="impact-walkin-name" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <input placeholder="Email" value={walkin.email} onChange={e => setWalkin(w => ({ ...w, email: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <input placeholder="Organization" value={walkin.organization} onChange={e => setWalkin(w => ({ ...w, organization: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <div className="flex gap-2">
                  <button onClick={addWalkin} data-testid="impact-walkin-save" className="px-3 py-2 rounded-lg text-xs font-bold text-white flex-1" style={{ background: BRAND.navy }}>Add</button>
                  <button onClick={() => setWalkin(null)} className="px-3 py-2 rounded-lg text-xs font-bold text-slate-500 border border-slate-200">Cancel</button>
                </div>
              </div>
            )}

            <table className="w-full text-sm" data-testid="impact-roster-table">
              <thead><tr className="text-left text-xs uppercase text-slate-500 border-b bg-slate-50">
                <th className="py-2.5 px-2 w-8"></th><th>Name</th><th>Email</th><th>Registration</th><th>Attendance</th><th>Completion</th>
              </tr></thead>
              <tbody>
                {data.roster.map(r => (
                  <tr key={r.id} className="border-b border-slate-100" data-testid={`impact-roster-row-${r.id}`}>
                    <td className="py-2.5 px-2"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`impact-roster-check-${r.id}`} /></td>
                    <td className="font-medium" style={{ color: BRAND.navy }}>{r.person_name}{r.person_org && <div className="text-[10px] font-normal text-slate-400">{r.person_org}</div>}</td>
                    <td className="text-xs text-slate-500">{r.person_email || '—'}</td>
                    <td className="text-xs capitalize">{(r.registration_status || '').replace('_', ' ')}</td>
                    <td>
                      <select value={r.attendance_status} onChange={e => setStatus(r.id, 'attendance_status', e.target.value)}
                        data-testid={`impact-roster-attendance-${r.id}`}
                        className={`border rounded-lg px-2 py-1 text-xs bg-white ${r.attendance_status === 'attended' ? 'border-green-400 text-green-700' : r.attendance_status === 'no_show' ? 'border-rose-300 text-rose-600' : 'border-slate-300 text-slate-500'}`}>
                        {STATUSES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={r.completion_status} onChange={e => setStatus(r.id, 'completion_status', e.target.value)}
                        data-testid={`impact-roster-completion-${r.id}`}
                        className="border border-slate-300 rounded-lg px-2 py-1 text-xs bg-white text-slate-500">
                        {COMPLETION.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
